import {t, Trans} from '@lingui/macro'
import {withFormik, FormikProps} from 'formik'
import * as R from 'ramda'
import React from 'react'

import {
    Button,
    Inline,
    ModalTitle,
    ModalContent,
    Stack,
    FormikInput as Input,
} from '@pleo-io/telescope'

import * as apiAuthUser from '~/api/auth/user'
import {Cell} from '~/generic-ui/layout/cell'
import {__legacyShowModal, IModal} from '~/shell/legacy/legacy-modals-bridge'

import validationSchema, {FormValues} from './reset-password.validation'

interface OwnProps {
    email?: string
}

type Props = OwnProps & IModal & FormikProps<FormValues>

interface State {
    success: boolean
    message: string
    fetching: boolean
}

class ModalResetPassword extends React.Component<Props, State> {
    state = {
        success: false,
        message: '',
        fetching: false,
    }

    requestResetPassword = (e: React.FormEvent<HTMLFormElement>) => {
        e && e.preventDefault()
        e && e.stopPropagation()

        const {fetching} = this.state
        const {values, errors} = this.props

        if (fetching || !R.isEmpty(errors)) {
            return
        }

        this.setState({fetching: true})
        apiAuthUser
            .requestResetPassword(values.email)
            .then(() => {
                this.setState({
                    success: true,
                    message: t`Check your email ${values.email} for a password reset link`,
                })
            })
            .catch((err) => {
                this.setState({fetching: false, success: false, message: 'failed'})
                setTimeout(() => {
                    throw err
                })
            })
    }

    reject = () => this.props._modal && this.props._modal.promise.reject()

    resolve = () => this.props._modal && this.props._modal.promise.resolve()

    render() {
        const {fetching, success, message} = this.state
        const {email, errors} = this.props

        if (success) {
            return <Success message={message} close={() => success && this.resolve()} />
        }

        return (
            <form onSubmit={this.requestResetPassword}>
                <ModalTitle>
                    <Trans>Reset Password</Trans>
                </ModalTitle>
                <ModalContent>
                    <Stack space={40} align="center" stretch>
                        <Input
                            autoFocus
                            name="email"
                            placeholder={t`Pleo account email`}
                            aria-label={t`Pleo account email`}
                            readOnly={!!email}
                            data-testid="email"
                            variant="naked"
                        />
                        <Inline alignX="center" space={16}>
                            <Button type="button" variant="secondary" onClick={this.reject}>
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                loading={fetching}
                                disabled={!R.isEmpty(errors)}
                            >
                                <Trans>Reset password</Trans>
                            </Button>
                        </Inline>
                    </Stack>
                </ModalContent>
            </form>
        )
    }
}

export const Success = ({message, close}: {message: string; close: () => void}) => (
    <>
        <ModalTitle>
            <Trans>Reset Password</Trans>
        </ModalTitle>
        <ModalContent>
            <Cell margin={40} center={true}>
                {message}
            </Cell>
            <Cell margin={20} center={true}>
                <Button type="button" variant="primary" onClick={close}>
                    <Trans>Close</Trans>
                </Button>
            </Cell>
        </ModalContent>
    </>
)

export const ModalResetPasswordForm = withFormik<OwnProps, FormValues>({
    handleSubmit: () => null,
    mapPropsToValues: ({email}) => ({
        email: email || '',
    }),
    validationSchema,
    validateOnMount: true,
    validateOnBlur: false,
})(ModalResetPassword)

export default {
    show: (email?: string) => __legacyShowModal(<ModalResetPasswordForm email={email} />),
}
